import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Paper,
  alpha,
  styled,
} from '@mui/material';
import {
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
  Science as TestIcon,
  AutoAwesome as AiIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ParsedPrescriptionData } from '../../types/prescription.types';

// Luxury color palette (shared)
const colors = {
  gold: '#C5A572',
  goldLight: '#E5D3B5',
  goldDark: '#8B7355',
  burgundy: '#800020',
  burgundyLight: '#A52A2A',
  navy: '#1A1F2E',
  navyLight: '#2A2F3F',
  cream: '#FDFAF3',
  creamDark: '#F5E6D3',
  charcoal: '#2C2C2C',
  silver: '#C0C0C0',
  emerald: '#046307',
  emeraldLight: '#2E7D32',
  platinum: '#E5E4E2',
  rose: '#B76E79',
  roseLight: '#DAB6B9',
};

// Styled components
const LuxuryCard = styled(Card)({
  background: `linear-gradient(135deg, ${colors.cream} 0%, #FFFFFF 100%)`,
  border: `1px solid ${colors.goldLight}`,
  borderRadius: '16px',
  boxShadow: `0 10px 30px -10px ${alpha(colors.gold, 0.2)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${colors.gold} 0%, ${colors.burgundy} 50%, ${colors.navy} 100%)`,
  },
});

const InfoChip = styled(Chip)({
  backgroundColor: alpha(colors.gold, 0.1),
  border: `1px solid ${colors.gold}`,
  color: colors.navy,
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: colors.gold,
  },
});

const ConfidenceBadge: React.FC<{ confidence: number }> = ({ confidence }) => {
  const getColor = () => {
    if (confidence >= 0.85) return colors.emerald;
    if (confidence >= 0.6) return colors.gold;
    return colors.rose;
  };

  return (
    <Chip
      icon={<AiIcon />}
      label={`AI Confidence: ${Math.round(confidence * 100)}%`}
      sx={{
        backgroundColor: alpha(getColor(), 0.1),
        border: `1px solid ${getColor()}`,
        color: colors.navy,
        fontWeight: 600,
        '& .MuiChip-icon': {
          color: getColor(),
        },
      }}
    />
  );
};

interface PrescriptionDisplayProps {
  data: ParsedPrescriptionData | null;
  onAutoFillPatient: () => void;
  onAutoSelectTests: () => void;
  onTestToggle: (testName: string) => void;
  selectedTests: string[];
}

export const PrescriptionDisplay: React.FC<PrescriptionDisplayProps> = ({
  data,
  onAutoFillPatient,
  onAutoSelectTests,
  onTestToggle,
  selectedTests,
}) => {
  if (!data) {
    return (
      <LuxuryCard>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No prescription data available. Please upload an image.
          </Typography>
        </CardContent>
      </LuxuryCard>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <LuxuryCard>
        <CardContent sx={{ p: 1 }}>
          {/* Header with confidence */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ color: colors.navy, fontWeight: 600 }}>
              Extracted Prescription Data
            </Typography>
            {data.confidence && <ConfidenceBadge confidence={data.confidence} />}
          </Box>

          <Divider sx={{ mb: 1, borderColor: alpha(colors.gold, 0.3) }} />

          {/* Patient Details Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: alpha(colors.platinum, 0.3),
              borderRadius: '12px',
              border: `1px solid ${alpha(colors.gold, 0.2)}`,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: colors.navy }}
            >
              <PersonIcon sx={{ color: colors.gold }} />
              Patient Information
            </Typography>
            <Divider sx={{ mb: 1, borderColor: alpha(colors.gold, 0.3) }} />
            <Grid container spacing={2}>
              <Grid size={{xs:12,sm:6}} >
                <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7) }}>
                  Full Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: colors.navy }}>
                  {data.patientName || 'Not found'}
                </Typography>
              </Grid>
              <Grid size={{xs:6,sm:3}}>
                <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7) }}>
                  Age
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: colors.navy }}>
                  {data.age || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{xs:6,sm:3}}>
                <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7) }}>
                  Gender
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: colors.navy }}>
                  {data.gender || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{xs:12,sm:6}} >
                <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7) }}>
                  Mobile Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: colors.navy, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {data.mobileNumber ? (
                    <>
                      <PhoneIcon sx={{ fontSize: 16, color: colors.gold }} />
                      {data.mobileNumber}
                    </>
                  ) : 'Not found'}
                </Typography>
              </Grid>
              <Grid size={{xs:12,sm:6}} >
                <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7) }}>
                  Referring Doctor
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: colors.navy, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {data.doctorName ? (
                    <>
                      <DoctorIcon sx={{ fontSize: 16, color: colors.gold }} />
                      {data.doctorName}
                    </>
                  ) : 'Not found'}
                </Typography>
              </Grid>
              <Grid size={{xs:12,sm:6}} >
                <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7) }}>
                  Prescription Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: colors.navy, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {data.date ? (
                    <>
                      <DateIcon sx={{ fontSize: 16, color: colors.gold }} />
                      {data.date}
                    </>
                  ) : 'Not found'}
                </Typography>
              </Grid>
            </Grid>
            <Box mt={1} display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<PersonIcon />}
                onClick={onAutoFillPatient}
                sx={{
                  borderColor: colors.gold,
                  color: colors.navy,
                  '&:hover': {
                    borderColor: colors.burgundy,
                    backgroundColor: alpha(colors.gold, 0.1),
                  },
                }}
              >
                Auto-fill Patient Details
              </Button>
            </Box>
          </Paper>

          {/* Tests Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: alpha(colors.platinum, 0.3),
              borderRadius: '12px',
              border: `1px solid ${alpha(colors.gold, 0.2)}`,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography
                variant="subtitle1"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.navy }}
              >
                <TestIcon sx={{ color: colors.gold }} />
                Recommended Tests
              </Typography>
              <Chip 
                label={`${data.tests?.length || 0} tests detected`}
                size="small"
                sx={{
                  backgroundColor: alpha(colors.gold, 0.1),
                  color: colors.navy,
                  border: `1px solid ${colors.gold}`,
                }}
              />
              <Divider sx={{ mb: 1, borderColor: alpha(colors.gold, 0.3) }} />
            </Box>
            
            <Box display="flex" flexWrap="wrap" gap={1} mb={3} minHeight="60px">
              {data.tests && data.tests.length > 0 ? (
                data.tests.map((test, index) => {
                  const testName = test.originalName || test.name || 'Unknown Test';
                  const isSelected = selectedTests.includes(testName);
                  const confidence = test.confidence || data.confidence || 0.85;
                  const testColor = confidence >= 0.85 ? colors.emerald : confidence >= 0.6 ? colors.gold : colors.rose;

                  return (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      
                      <Chip
                        label={testName}
                        onClick={() => onTestToggle(testName)}
                        icon={isSelected ? <CheckIcon /> : undefined}
                        sx={{
                          backgroundColor: isSelected ? alpha(testColor, 0.2) : 'transparent',
                          border: `2px solid ${testColor}`,
                          color: colors.navy,
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: alpha(testColor, 0.1),
                          },
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    </motion.div>
                  );
                })
              ) : (
                <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.5), py: 2 }}>
                  No tests detected in the prescription.
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2, borderColor: alpha(colors.gold, 0.2) }} />

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" sx={{ color: alpha(colors.charcoal, 0.7) }}>
                {selectedTests.length} tests currently selected
              </Typography>
              <Button
                variant="contained"
                startIcon={<AiIcon />}
                onClick={onAutoSelectTests}
                disabled={!data.tests || data.tests.length === 0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.burgundy} 100%)`,
                  color: colors.cream,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${colors.burgundy} 0%, ${colors.gold} 100%)`,
                  },
                  '&.Mui-disabled': {
                    background: alpha(colors.gold, 0.3),
                    color: alpha(colors.cream, 0.5),
                  },
                }}
              >
                Auto-select All Tests
              </Button>
              <Divider sx={{ mb: 1, borderColor: alpha(colors.gold, 0.3) }} />
            </Box>
          </Paper>

          {/* Diagnosis Section (if available) */}
          {data.doctorName && (
            <Box mt={3}>
              <InfoChip
                label={`Diagnosis: ${data.doctorName}`}
                icon={<DoctorIcon />}
              />
            </Box>
          )}

          {/* Unmatched Tests Warning (if any) */}
          {data.unmatchedTests && data.unmatchedTests.length > 0 && (
            <Box mt={3}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: alpha(colors.rose, 0.1),
                  border: `1px solid ${colors.rose}`,
                  borderRadius: '8px',
                }}
              >
                <Typography variant="body2" sx={{ color: colors.rose, fontWeight: 600, mb: 1 }}>
                  Tests that couldn't be matched:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {data.unmatchedTests.map((test, index) => (
                    <Chip
                      key={index}
                      label={test}
                      size="small"
                      sx={{
                        backgroundColor: alpha(colors.rose, 0.1),
                        border: `1px solid ${colors.rose}`,
                        color: colors.rose,
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Box>
          )}
        </CardContent>
      </LuxuryCard>
    </motion.div>
  );
};

// import React, { useState } from 'react';
// import {
//   Box,
//   Paper,
//   Typography,
//   Chip,
//   Grid,
//   Divider,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Button,
//   Tooltip,
//   Alert,
// } from '@mui/material';
// import {
//   Science as ScienceIcon,
//   Person as PersonIcon,
//   LocalHospital as LocalHospitalIcon,
//   Phone as PhoneIcon,
//   CalendarToday as CalendarIcon,
//   Female as FemaleIcon,
//   Male as MaleIcon,
//   Assignment as AssignmentIcon,
//   CheckCircle as CheckCircleIcon,
//   Error as ErrorIcon,
//   Warning as WarningIcon,
//   ExpandMore as ExpandMoreIcon,
//   ExpandLess as ExpandLessIcon,
// } from '@mui/icons-material';
// import { PrescriptionDisplayProps } from '../../types/prescription.types';

// // Confidence Badge Component
// const ConfidenceBadge: React.FC<{ confidence: number }> = ({ confidence }) => {
//   const getColor = () => {
//     if (confidence >= 0.85) return 'success';
//     if (confidence >= 0.6) return 'warning';
//     return 'error';
//   };

//   const getIcon = () => {
//     if (confidence >= 0.85) return <CheckCircleIcon fontSize="small" />;
//     if (confidence >= 0.6) return <WarningIcon fontSize="small" />;
//     return <ErrorIcon fontSize="small" />;
//   };

//   return (
//     <Tooltip title={`AI Confidence: ${Math.round(confidence * 100)}%`}>
//       <Chip
//         icon={getIcon()}
//         label={`${Math.round(confidence * 100)}%`}
//         color={getColor()}
//         size="small"
//         variant="outlined"
//         sx={{ fontWeight: 'bold' }}
//       />
//     </Tooltip>
//   );
// };

// // Test Row Component
// const TestRow: React.FC<{
//   test: any;
//   index: number;
//   isSelected?: boolean;
//   onToggle?: (testName: string) => void;
// }> = ({ test, index, isSelected = false, onToggle }) => {
//   const confidence = test.confidence || 0.85;
//   const testName = test.name;
  
//   const getBackgroundColor = () => {
//     if (confidence >= 0.85) return 'rgba(76, 175, 80, 0.08)';
//     if (confidence >= 0.6) return 'rgba(255, 152, 0, 0.08)';
//     return 'rgba(244, 67, 54, 0.08)';
//   };

//   return (
//     <TableRow 
//       key={`test-${index}-${testName}`}
//       hover
//       sx={{
//         bgcolor: getBackgroundColor(),
//         transition: 'background-color 0.2s',
//         '&:hover': {
//           bgcolor: 'action.hover',
//           cursor: onToggle ? 'pointer' : 'default',
//         }
//       }}
//       onClick={() => onToggle && testName && onToggle(testName)}
//     >
//       <TableCell>
//         <Typography variant="body2" sx={{ fontWeight: 500 }}>
//           {testName}
//         </Typography>
//       </TableCell>
//       <TableCell align="center">
//         <ConfidenceBadge confidence={confidence} />
//       </TableCell>
//       {onToggle && (
//         <TableCell align="center">
//           <Chip
//             label={isSelected ? 'Selected' : 'Available'}
//             color={isSelected ? 'success' : 'default'}
//             size="small"
//             variant={isSelected ? 'filled' : 'outlined'}
//           />
//         </TableCell>
//       )}
//     </TableRow>
//   );
// };

// export const PrescriptionDisplay: React.FC<PrescriptionDisplayProps> = ({ 
//   data, 
//   onAutoFillPatient,
//   onAutoSelectTests,
//   onTestToggle,
//   selectedTests = []
// }) => {
//   const [showAllTests, setShowAllTests] = useState(false);
  
//   if (!data) return null;

//   // Calculate confidence distribution
//   const highConfTests = data.tests?.filter(t => (t.confidence || 0.85) >= 0.85).length || 0;
//   const medConfTests = data.tests?.filter(t => {
//     const conf = t.confidence || 0.85;
//     return conf >= 0.6 && conf < 0.85;
//   }).length || 0;
//   const lowConfTests = data.tests?.filter(t => (t.confidence || 0.85) < 0.6).length || 0;
  
//   const totalTests = data.tests?.length || 0;
//   const avgConfidence = data.confidence || 
//     (data.tests?.reduce((sum, t) => sum + (t.confidence || 0.85), 0) || 0) / (totalTests || 1);

//   return (
//     <Paper elevation={3} sx={{ p: 3, mt: 2, bgcolor: '#f8f9fa' }}>
//       {/* Header with Overall Confidence */}
//       <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//         <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
//           <AssignmentIcon />
//           📋 Extracted Prescription Data
//         </Typography>
//         {avgConfidence > 0 && (
//           <Chip
//             icon={avgConfidence >= 0.85 ? <CheckCircleIcon /> : avgConfidence >= 0.6 ? <WarningIcon /> : <ErrorIcon />}
//             label={`Overall: ${Math.round(avgConfidence * 100)}%`}
//             color={avgConfidence >= 0.85 ? 'success' : avgConfidence >= 0.6 ? 'warning' : 'error'}
//             variant="filled"
//             sx={{ fontWeight: 'bold' }}
//           />
//         )}
//       </Box>

//       {/* Confidence Distribution */}
//       {totalTests > 0 && (
//         <Alert 
//           severity={lowConfTests > 0 ? 'warning' : 'success'}
//           sx={{ mb: 3 }}
//           icon={lowConfTests > 0 ? <WarningIcon /> : <CheckCircleIcon />}
//         >
//           <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
//             <Typography variant="body2">
//               Test Confidence:
//             </Typography>
//             <Box display="flex" gap={1}>
//               <Chip 
//                 size="small" 
//                 label={`High: ${highConfTests}`} 
//                 color="success" 
//                 variant="outlined"
//               />
//               <Chip 
//                 size="small" 
//                 label={`Medium: ${medConfTests}`} 
//                 color="warning" 
//                 variant="outlined"
//               />
//               <Chip 
//                 size="small" 
//                 label={`Low: ${lowConfTests}`} 
//                 color="error" 
//                 variant="outlined"
//               />
//             </Box>
//             {lowConfTests > 0 && (
//               <Typography variant="caption" color="warning.main">
//                 ⚠️ {lowConfTests} test(s) need verification
//               </Typography>
//             )}
//           </Box>
//         </Alert>
//       )}

//       {/* Patient Info Section */}
//       {(data.patientName || data.age || data.gender || data.mobileNumber || data.doctorName || data.date) && (
//         <>
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, mb: 2 }}>
//               <PersonIcon color="primary" />
//               Patient Information
//             </Typography>
//             <Grid container spacing={2}>
//               {data.patientName && (
//                 <Grid size={{ xs: 12, sm: 6, md: 4 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Patient Name</Typography>
//                     <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.patientName}</Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.age && (
//                 <Grid size={{ xs: 6, sm: 3, md: 2 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Age</Typography>
//                     <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.age} yrs</Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.gender && (
//                 <Grid size={{ xs: 6, sm: 3, md: 2 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Gender</Typography>
//                     <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                       {data.gender === 'Male' ? <MaleIcon fontSize="small" /> : <FemaleIcon fontSize="small" />}
//                       {data.gender}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.mobileNumber && (
//                 <Grid size={{ xs: 12, sm: 6, md: 4 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Mobile Number</Typography>
//                     <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                       <PhoneIcon fontSize="small" color="action" />
//                       {data.mobileNumber}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.doctorName && (
//                 <Grid size={{ xs: 12, sm: 6, md: 4 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Referring Doctor</Typography>
//                     <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                       <LocalHospitalIcon fontSize="small" color="action" />
//                       {data.doctorName}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.date && (
//                 <Grid size={{ xs: 12, sm: 6, md: 4 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Prescription Date</Typography>
//                     <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                       <CalendarIcon fontSize="small" color="action" />
//                       {data.date}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               )}
//             </Grid>
//           </Box>
//           <Divider sx={{ my: 2 }} />
//         </>
//       )}

//       {/* Tests Section with Confidence */}
//       {data.tests && data.tests.length > 0 && (
//         <>
//           <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//             <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
//               <ScienceIcon color="primary" />
//               Detected Tests ({data.tests.length})
//             </Typography>
//             <Button
//               size="small"
//               endIcon={showAllTests ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//               onClick={() => setShowAllTests(!showAllTests)}
//             >
//               {showAllTests ? 'Show Less' : 'Show All'}
//             </Button>
//           </Box>
          
//           <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
//             <Table size="small">
//               <TableHead>
//                 <TableRow sx={{ bgcolor: '#f5f5f5' }}>
//                   <TableCell width="70%"><strong>Test Name</strong></TableCell>
//                   <TableCell width="30%" align="center"><strong>Confidence</strong></TableCell>
//                   {onTestToggle && (
//                     <TableCell width="30%" align="center"><strong>Status</strong></TableCell>
//                   )}
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {/* Show high confidence tests (always visible) */}
//                 {data.tests
//                   .filter(t => (t.confidence || 0.85) >= 0.85)
//                   .map((test, idx) => (
//                     <TestRow
//                       key={`high-${idx}-${test.name}`}
//                       test={test}
//                       index={idx}
//                       isSelected={selectedTests.includes(test.name)}
//                       onToggle={onTestToggle}
//                     />
//                   ))}

//                 {/* Show medium confidence tests (if expanded) */}
//                 {showAllTests && data.tests
//                   .filter(t => {
//                     const conf = t.confidence || 0.85;
//                     return conf >= 0.6 && conf < 0.85;
//                   })
//                   .map((test, idx) => (
//                     <TestRow
//                       key={`med-${idx}-${test.name}`}
//                       test={test}
//                       index={idx}
//                       isSelected={selectedTests.includes(test.name)}
//                       onToggle={onTestToggle}
//                     />
//                   ))}

//                 {/* Show low confidence tests (if expanded) */}
//                 {showAllTests && data.tests
//                   .filter(t => (t.confidence || 0.85) < 0.6)
//                   .map((test, idx) => (
//                     <TestRow
//                       key={`low-${idx}-${test.name}`}
//                       test={test}
//                       index={idx}
//                       isSelected={selectedTests.includes(test.name)}
//                       onToggle={onTestToggle}
//                     />
//                   ))}

//                 {data.tests.length === 0 && (
//                   <TableRow>
//                     <TableCell colSpan={onTestToggle ? 3 : 2} align="center">
//                       <Typography color="textSecondary" sx={{ py: 2 }}>
//                         No tests detected in prescription
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 )}
//               </TableBody>
//             </Table>
//           </TableContainer>
//         </>
//       )}

//       {/* Quick Actions */}
//       <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
//         <Button
//           size="medium"
//           variant="outlined"
//           startIcon={<PersonIcon />}
//           onClick={onAutoFillPatient}
//           color="primary"
//         >
//           Auto-fill Patient
//         </Button>
//         <Button
//           size="medium"
//           variant="contained"
//           color="secondary"
//           startIcon={<ScienceIcon />}
//           onClick={onAutoSelectTests}
//         >
//           Auto-select Tests
//         </Button>
//       </Box>

//       {/* Manual Verification Notice */}
//       {lowConfTests > 0 && (
//         <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
//           <Typography variant="body2" color="warning.dark" display="flex" alignItems="center" gap={1}>
//             <WarningIcon fontSize="small" />
//             <strong>Manual verification required:</strong> {lowConfTests} test(s) with low confidence need your review.
//             {onTestToggle && ' Click on any test to toggle selection.'}
//           </Typography>
//         </Box>
//       )}
//     </Paper>
//   );
// };

// import React, { useState } from 'react';
// import {
//   Box,
//   Paper,
//   Typography,
//   Chip,
//   Grid,
//   Divider,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Button,
//   Tooltip,
//   Alert,
// } from '@mui/material';
// import {
//   Science as ScienceIcon,
//   Person as PersonIcon,
//   LocalHospital as LocalHospitalIcon,
//   Phone as PhoneIcon,
//   CalendarToday as CalendarIcon,
//   Female as FemaleIcon,
//   Male as MaleIcon,
//   Assignment as AssignmentIcon,
//   CheckCircle as CheckCircleIcon,
//   Error as ErrorIcon,
//   Warning as WarningIcon,
//   ExpandMore as ExpandMoreIcon,
//   ExpandLess as ExpandLessIcon,
// } from '@mui/icons-material';
// import { 
//   PrescriptionDisplayProps,
//   TestItem,
//   ParsedTest
// } from '../../types/lab.types';

// // Confidence Badge Component
// const ConfidenceBadge: React.FC<{ confidence: number }> = ({ confidence }) => {
//   const getColor = () => {
//     if (confidence >= 0.85) return 'success';
//     if (confidence >= 0.6) return 'warning';
//     return 'error';
//   };

//   const getIcon = () => {
//     if (confidence >= 0.85) return <CheckCircleIcon fontSize="small" />;
//     if (confidence >= 0.6) return <WarningIcon fontSize="small" />;
//     return <ErrorIcon fontSize="small" />;
//   };

//   return (
//     <Tooltip title={`AI Confidence: ${Math.round(confidence * 100)}%`}>
//       <Chip
//         icon={getIcon()}
//         label={`${Math.round(confidence * 100)}%`}
//         color={getColor()}
//         size="small"
//         variant="outlined"
//         sx={{ fontWeight: 'bold' }}
//       />
//     </Tooltip>
//   );
// };

// // Test Row Component
// const TestRowWithConfidence: React.FC<{
//   test: TestItem | ParsedTest;
//   index: number;
//   isSelected?: boolean;
//   onToggle?: (testName: string) => void;
// }> = ({ test, index, isSelected = false, onToggle }) => {
//   // Handle both TestItem and ParsedTest types
//   const confidence = 'confidence' in test ? test.confidence || 0.85 : 0.85;
//   const testName = test.name;
//   const testCode = 'code' in test ? test.code : undefined;
//   const recommended = 'recommended' in test ? test.recommended : true;
  
//   const getBackgroundColor = () => {
//     if (confidence >= 0.85) return 'rgba(76, 175, 80, 0.08)';
//     if (confidence >= 0.6) return 'rgba(255, 152, 0, 0.08)';
//     return 'rgba(244, 67, 54, 0.08)';
//   };

//   return (
//     <TableRow 
//       key={`test-${index}-${testName}`}
//       hover
//       sx={{
//         bgcolor: getBackgroundColor(),
//         transition: 'background-color 0.2s',
//         '&:hover': {
//           bgcolor: 'action.hover',
//           cursor: onToggle ? 'pointer' : 'default',
//         }
//       }}
//       onClick={() => onToggle && testName && onToggle(testName)}
//     >
//       <TableCell>
//         <Box display="flex" alignItems="center" gap={1}>
//           <Typography variant="body2" sx={{ fontWeight: 500 }}>
//             {testName}
//           </Typography>
//           {recommended && (
//             <Chip 
//               label="Recommended" 
//               size="small" 
//               color="primary" 
//               variant="outlined"
//               sx={{ height: 20, fontSize: '0.7rem' }}
//             />
//           )}
//         </Box>
//       </TableCell>
//       <TableCell>
//         {testCode ? (
//           <Chip 
//             label={testCode} 
//             size="small" 
//             variant="outlined"
//             sx={{ fontFamily: 'monospace' }}
//           />
//         ) : '-'}
//       </TableCell>
//       <TableCell align="center">
//         <ConfidenceBadge confidence={confidence} />
//       </TableCell>
//       {onToggle && (
//         <TableCell align="center">
//           <Chip
//             label={isSelected ? 'Selected' : 'Available'}
//             color={isSelected ? 'success' : 'default'}
//             size="small"
//             variant={isSelected ? 'filled' : 'outlined'}
//           />
//         </TableCell>
//       )}
//     </TableRow>
//   );
// };

// export const PrescriptionDisplay: React.FC<PrescriptionDisplayProps> = ({ 
//   data, 
//   onAutoFillPatient,
//   onAutoSelectTests,
//   onTestToggle,
//   selectedTests = []
// }) => {
//   const [showAllTests, setShowAllTests] = useState(false);
  
//   if (!data) return null;

//   // Calculate confidence distribution
//   const highConfTests = data.tests?.filter(t => (t.confidence || 0.85) >= 0.85).length || 0;
//   const medConfTests = data.tests?.filter(t => {
//     const conf = t.confidence || 0.85;
//     return conf >= 0.6 && conf < 0.85;
//   }).length || 0;
//   const lowConfTests = data.tests?.filter(t => (t.confidence || 0.85) < 0.6).length || 0;
  
//   const totalTests = data.tests?.length || 0;
//   const avgConfidence = data.confidence || 
//     (data.tests?.reduce((sum, t) => sum + (t.confidence || 0.85), 0) || 0) / (totalTests || 1);

//   return (
//     <Paper elevation={3} sx={{ p: 3, mt: 2, bgcolor: '#f8f9fa' }}>
//       {/* Header with Overall Confidence */}
//       <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//         <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
//           <AssignmentIcon />
//           📋 Extracted Prescription Data
//         </Typography>
//         {avgConfidence > 0 && (
//           <Chip
//             icon={avgConfidence >= 0.85 ? <CheckCircleIcon /> : avgConfidence >= 0.6 ? <WarningIcon /> : <ErrorIcon />}
//             label={`Overall: ${Math.round(avgConfidence * 100)}%`}
//             color={avgConfidence >= 0.85 ? 'success' : avgConfidence >= 0.6 ? 'warning' : 'error'}
//             variant="filled"
//             sx={{ fontWeight: 'bold' }}
//           />
//         )}
//       </Box>

//       {/* Confidence Distribution */}
//       {totalTests > 0 && (
//         <Alert 
//           severity={lowConfTests > 0 ? 'warning' : 'success'}
//           sx={{ mb: 3 }}
//           icon={lowConfTests > 0 ? <WarningIcon /> : <CheckCircleIcon />}
//         >
//           <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
//             <Typography variant="body2">
//               Test Confidence:
//             </Typography>
//             <Box display="flex" gap={1}>
//               <Chip 
//                 size="small" 
//                 label={`High: ${highConfTests}`} 
//                 color="success" 
//                 variant="outlined"
//               />
//               <Chip 
//                 size="small" 
//                 label={`Medium: ${medConfTests}`} 
//                 color="warning" 
//                 variant="outlined"
//               />
//               <Chip 
//                 size="small" 
//                 label={`Low: ${lowConfTests}`} 
//                 color="error" 
//                 variant="outlined"
//               />
//             </Box>
//             {lowConfTests > 0 && (
//               <Typography variant="caption" color="warning.main">
//                 ⚠️ {lowConfTests} test(s) need verification
//               </Typography>
//             )}
//           </Box>
//         </Alert>
//       )}

//       {/* Patient Info Section */}
//       {(data.patientName || data.age || data.gender || data.mobileNumber || data.doctorName || data.date) && (
//         <>
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, mb: 2 }}>
//               <PersonIcon color="primary" />
//               Patient Information
//             </Typography>
//             <Grid container spacing={2}>
//               {data.patientName && (
//                 <Grid size={{ xs: 12, sm: 6, md: 4 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Patient Name</Typography>
//                     <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.patientName}</Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.age && (
//                 <Grid size={{ xs: 6, sm: 3, md: 2 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Age</Typography>
//                     <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.age} yrs</Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.gender && (
//                 <Grid size={{ xs: 6, sm: 3, md: 2 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Gender</Typography>
//                     <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                       {data.gender === 'Male' ? <MaleIcon fontSize="small" /> : <FemaleIcon fontSize="small" />}
//                       {data.gender}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.mobileNumber && (
//                 <Grid size={{ xs: 12, sm: 6, md: 4 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Mobile Number</Typography>
//                     <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                       <PhoneIcon fontSize="small" color="action" />
//                       {data.mobileNumber}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.doctorName && (
//                 <Grid size={{ xs: 12, sm: 6, md: 4 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Referring Doctor</Typography>
//                     <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                       <LocalHospitalIcon fontSize="small" color="action" />
//                       {data.doctorName}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               )}
//               {data.date && (
//                 <Grid size={{ xs: 12, sm: 6, md: 4 }}>
//                   <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
//                     <Typography variant="caption" color="textSecondary">Prescription Date</Typography>
//                     <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                       <CalendarIcon fontSize="small" color="action" />
//                       {data.date}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               )}
//             </Grid>
//           </Box>
//           <Divider sx={{ my: 2 }} />
//         </>
//       )}

//       {/* Tests Section with Confidence */}
//       {data.tests && data.tests.length > 0 && (
//         <>
//           <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//             <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
//               <ScienceIcon color="primary" />
//               Detected Tests ({data.tests.length})
//             </Typography>
//             <Button
//               size="small"
//               endIcon={showAllTests ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//               onClick={() => setShowAllTests(!showAllTests)}
//             >
//               {showAllTests ? 'Show Less' : 'Show All'}
//             </Button>
//           </Box>
          
//           <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
//             <Table size="small">
//               <TableHead>
//                 <TableRow sx={{ bgcolor: '#f5f5f5' }}>
//                   <TableCell width="45%"><strong>Test Name</strong></TableCell>
//                   <TableCell width="20%"><strong>Code</strong></TableCell>
//                   <TableCell width="20%" align="center"><strong>Confidence</strong></TableCell>
//                   {onTestToggle && (
//                     <TableCell width="15%" align="center"><strong>Status</strong></TableCell>
//                   )}
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {/* Show high confidence tests (always visible) */}
//                 {data.tests
//                   .filter(t => (t.confidence || 0.85) >= 0.85)
//                   .map((test, idx) => (
//                     <TestRowWithConfidence
//                       key={`high-${idx}-${test.name}`}
//                       test={test}
//                       index={idx}
//                       isSelected={selectedTests.includes(test.name)}
//                       onToggle={onTestToggle}
//                     />
//                   ))}

//                 {/* Show medium confidence tests (if expanded) */}
//                 {showAllTests && data.tests
//                   .filter(t => {
//                     const conf = t.confidence || 0.85;
//                     return conf >= 0.6 && conf < 0.85;
//                   })
//                   .map((test, idx) => (
//                     <TestRowWithConfidence
//                       key={`med-${idx}-${test.name}`}
//                       test={test}
//                       index={idx}
//                       isSelected={selectedTests.includes(test.name)}
//                       onToggle={onTestToggle}
//                     />
//                   ))}

//                 {/* Show low confidence tests (if expanded) */}
//                 {showAllTests && data.tests
//                   .filter(t => (t.confidence || 0.85) < 0.6)
//                   .map((test, idx) => (
//                     <TestRowWithConfidence
//                       key={`low-${idx}-${test.name}`}
//                       test={test}
//                       index={idx}
//                       isSelected={selectedTests.includes(test.name)}
//                       onToggle={onTestToggle}
//                     />
//                   ))}

//                 {data.tests.length === 0 && (
//                   <TableRow>
//                     <TableCell colSpan={onTestToggle ? 4 : 3} align="center">
//                       <Typography color="textSecondary" sx={{ py: 2 }}>
//                         No tests detected in prescription
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 )}
//               </TableBody>
//             </Table>
//           </TableContainer>
//         </>
//       )}

//       {/* Quick Actions */}
//       <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
//         <Button
//           size="medium"
//           variant="outlined"
//           startIcon={<PersonIcon />}
//           onClick={onAutoFillPatient}
//           color="primary"
//         >
//           Auto-fill Patient
//         </Button>
//         <Button
//           size="medium"
//           variant="contained"
//           color="secondary"
//           startIcon={<ScienceIcon />}
//           onClick={onAutoSelectTests}
//         >
//           Auto-select Tests
//         </Button>
//       </Box>

//       {/* Manual Verification Notice */}
//       {lowConfTests > 0 && (
//         <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
//           <Typography variant="body2" color="warning.dark" display="flex" alignItems="center" gap={1}>
//             <WarningIcon fontSize="small" />
//             <strong>Manual verification required:</strong> {lowConfTests} test(s) with low confidence need your review.
//             {onTestToggle && ' Click on any test to toggle selection.'}
//           </Typography>
//         </Box>
//       )}
//     </Paper>
//   );
// };